import type { Material } from "./appearance.ts";
import type { ClayKind } from "./clay.ts";
import type { RequestRecord } from "./records.ts";
import { REQUESTS } from "./requests.ts";
import { TARGET_DEFS } from "./targets.ts";

/** Progression is about different pieces delivered, never repetitions or AI judgements. */
export const ORDINARY_GROUPS: readonly (readonly string[])[] = [
  [
    "jomonyan-replica",
    "brewery-gourd",
    "festival-snowman",
    "nursery-snowman",
    "grocer-peanut",
    "lottery-bell",
  ],
  ["park-can", "snow-kamakura", "bakery-cornet", "grocer-bean-loop", "future-square-mug"],
  ["grocer-face-panel", "tumulus-trousers"],
  ["kitchen-onigiri-mold", "bakery-pretzel", "future-outlet-plate"],
  ["craft-big-button"],
];

export interface Challenge {
  request: string;
  title: string;
  needed: number;
  /** Optional mastery only: it never closes a letter or ends an attempt. */
  maxStrokes: number;
  lesson: string;
}

export const CHALLENGES: readonly Challenge[] = [
  {
    request: "cafe-mug",
    title: "ひとつの穴から",
    needed: 3,
    maxStrokes: 100,
    lesson: "穴を残したまま、取っ手のある器にこねてみよう。",
  },
  {
    request: "boutique-sunglasses",
    title: "ふたつの穴を並べて",
    needed: 2,
    maxStrokes: 120,
    lesson: "ふたつの穴をそろえて、ねこみみつきのサングラスをこねよう。",
  },
  {
    request: "tumulus-mask",
    title: "みっつの穴で表情を",
    needed: 1,
    maxStrokes: 140,
    lesson: "みっつの穴を動かして、お面の表情をこねてみよう。",
  },
  {
    request: "archive-window-frame",
    title: "よっつの穴をそろえて",
    needed: 2,
    maxStrokes: 160,
    lesson: "よっつの穴を田の字にそろえて、まっすぐな桟の窓枠をこねよう。",
  },
  {
    request: "tumulus-kirin",
    title: "よっつの穴で走らせて",
    needed: 1,
    maxStrokes: 160,
    lesson: "よっつの穴を横一列に並べ替えて、車輪で走るキリンをこねよう。",
  },
];

export const CLEAR_POINTS = 70, MASTERY_POINTS = 100;
export type Records = ReadonlyMap<string, RequestRecord>;
export const challengeFor = (id: string) => CHALLENGES.find((c) => c.request === id);
export const wasDelivered = (record?: RequestRecord) => !!record && record.deliveries > 0;
export const cleared = (record?: RequestRecord) =>
  wasDelivered(record) && record!.bestTotal >= CLEAR_POINTS;

/** These values belong to ONE delivered attempt, never to two separate bests. */
export function meetsMastery(id: string, total: number, strokes: number): boolean {
  const challenge = challengeFor(id);
  return !!challenge && Number.isFinite(total) && total >= MASTERY_POINTS &&
    Number.isInteger(strokes) && strokes >= 0 && strokes <= challenge.maxStrokes;
}

/** Old saves already store bestTotal/bestStrokes together, so that pair is safe to migrate. */
export const mastered = (id: string, record?: RequestRecord) =>
  wasDelivered(record) &&
  (!!record!.mastered || meetsMastery(id, record!.bestTotal, record!.bestStrokes));

export interface RequestAccess {
  available: boolean;
  reason: string;
}

/**
 * A previous delivery in a later group keeps that group and its predecessors open. This
 * preserves older saves made when every letter was open, without fabricating challenge clears.
 * A delivered challenge alone preserves access to that challenge, even if it was below B.
 */
export function accessTo(id: string, records: Records): RequestAccess {
  // Before sunglasses became the intro, any delivery meant the two-hole group was open.
  const glasses = records.get("boutique-sunglasses");
  const legacyGlasses = wasDelivered(glasses) && !glasses!.sunglassesIntro;
  let reached = legacyGlasses ? 2 : 0;
  for (let i = 1; i < ORDINARY_GROUPS.length; i++) {
    if (ORDINARY_GROUPS[i].some((r) => wasDelivered(records.get(r)))) {
      reached = Math.max(reached, i);
    }
  }
  for (let i = 1; i < CHALLENGES.length; i++) {
    if (wasDelivered(records.get(CHALLENGES[i].request))) reached = Math.max(reached, i);
  }
  const groupOpen = (i: number) =>
    i === 0 || reached >= i || cleared(records.get(CHALLENGES[i - 1].request));
  if (wasDelivered(records.get(id))) return { available: true, reason: "納品済み" };
  const ordinary = ORDINARY_GROUPS.findIndex((g) => g.includes(id));
  if (ordinary >= 0) {
    return groupOpen(ordinary) ? { available: true, reason: "" } : {
      available: false,
      reason: `お題「${CHALLENGES[ordinary - 1].title}」をB以上で納品すると届きます`,
    };
  }
  const index = CHALLENGES.findIndex((c) => c.request === id);
  if (index < 0) return { available: false, reason: "まだ届いていません" };
  const challenge = CHALLENGES[index];
  if (!groupOpen(index)) {
    return {
      available: false,
      reason: `前のお題「${CHALLENGES[index - 1].title}」をB以上で納品しよう`,
    };
  }
  const count = ORDINARY_GROUPS[index].filter((r) => wasDelivered(records.get(r))).length +
    (index === 2 && legacyGlasses ? 1 : 0);
  if (reached > index || cleared(records.get(id)) || count >= challenge.needed) {
    return { available: true, reason: "" };
  }
  return {
    available: false,
    reason:
      `穴${index}の通常依頼を${challenge.needed}種類納品すると届きます（${count}/${challenge.needed}）`,
  };
}

export function progressSummary(records: Records): string {
  for (const challenge of CHALLENGES) {
    if (cleared(records.get(challenge.request))) continue;
    const access = accessTo(challenge.request, records);
    return access.available
      ? `次のお題「${challenge.title}」: B以上（${CLEAR_POINTS}点）で納品しよう。手数は自由です。`
      : access.reason;
  }
  return "いつつのお題を達成！ 今届いている依頼はひと区切り。好きな依頼や腕試しを何度でもどうぞ。";
}

/** A request is cleared once delivered, a challenge once delivered at B or better. */
const requestCleared = (id: string, records: Records) =>
  challengeFor(id) ? cleared(records.get(id)) : wasDelivered(records.get(id));

/**
 * What free kneading can start from and be finished as: the lump and ceramic from the first,
 * then each cleared request's clay and material.
 */
export function freeKneading(records: Records): {
  clays: Set<ClayKind>;
  materials: Set<Material>;
} {
  const clays = new Set<ClayKind>(["blob"]), materials = new Set<Material>(["ceramic"]);
  for (const r of REQUESTS) {
    if (!requestCleared(r.id, records)) continue;
    clays.add(TARGET_DEFS.find((t) => t.id === r.target)!.clay);
    materials.add(r.appearance.material);
  }
  return { clays, materials };
}

/** The kitten's titles on its résumé, each from so many challenges cleared. */
export const KITTEN_TITLES: readonly { name: string; challenges: number }[] = [
  { name: "見習いこねこ", challenges: 0 },
  { name: "こね職人", challenges: 2 },
  { name: "こね師", challenges: CHALLENGES.length },
];

/** A badge on the résumé: what it is called, how it is earned, and whether it has been. */
export interface Badge {
  name: string;
  how: string;
  earned: boolean;
}

/** How far each part of the game has come, as so many out of all. */
export interface Rate {
  label: string;
  done: number;
  of: number;
}

/** What the kitten's résumé says, all from the ledger. */
export interface Resume {
  title: string;
  /** The next title and how many more challenges it needs (null at the top). */
  next: { name: string; more: number } | null;
  rates: Rate[];
  badges: Badge[];
}

export function resumeOf(records: Records): Resume {
  const known = REQUESTS.map((r) => records.get(r.id)).filter((r): r is RequestRecord =>
    wasDelivered(r)
  );
  const clears = CHALLENGES.filter((c) => cleared(records.get(c.request))).length;
  const masteries = CHALLENGES.filter((c) => mastered(c.request, records.get(c.request))).length;
  const wishes = known.filter((r) => r.wishMet).length;
  const all = REQUESTS.length;
  const reached = KITTEN_TITLES.filter((t) => clears >= t.challenges);
  const next = KITTEN_TITLES[reached.length];
  return {
    title: reached.at(-1)!.name,
    next: next ? { name: next.name, more: next.challenges - clears } : null,
    rates: [
      { label: "納品した依頼", done: known.length, of: all },
      { label: "叶えたこだわり", done: wishes, of: all },
      { label: "達成したお題", done: clears, of: CHALLENGES.length },
      { label: "腕試しのスタンプ", done: masteries, of: CHALLENGES.length },
    ],
    badges: [
      { name: "はじめての納品", how: "依頼をひとつ納品する", earned: known.length > 0 },
      {
        name: "トポロジスト",
        how: "形はまだでも、穴をおてほんの所に合わせて納品する",
        earned: known.some((r) => r.topological),
      },
      {
        name: "伝説のこねこ",
        how: "SSランクで納品する",
        earned: known.some((r) => r.bestRank === "SS"),
      },
      {
        name: "働き者",
        how: "あわせて50回納品する",
        earned: known.reduce((n, r) => n + r.deliveries, 0) >= 50,
      },
      { name: "こだわり名人", how: "すべての依頼のこだわりを叶える", earned: wishes === all },
      {
        name: "腕試し名人",
        how: "いつつの腕試しをすべて達成する",
        earned: masteries === CHALLENGES.length,
      },
      {
        name: "全部Sランク",
        how: "すべての依頼をSランク以上で納品する",
        earned: known.length === all && known.every((r) => ["S", "SS"].includes(r.bestRank)),
      },
    ],
  };
}
